import { useState } from 'react';
import { Button, ConfirmDialog, Alert } from '@dreamtree-org/twreact-ui';
import { SectionCard, StatusBadge } from '@/components';
import { formatDate } from '@/lib/format';
import DataTable from '../../components/DataTable';

/**
 * Domains (REAL). Custom domains wait for DNS verification; the TXT record to
 * publish comes from the API. "Mark verified" is the manual step until TXT
 * checks are automated (backlog).
 */
export default function DomainsTab({ merchant: m, act, busy, canManage }) {
  const [confirm, setConfirm] = useState(null);
  const [copied, setCopied] = useState('');
  const pending = (m.domains || []).filter((d) => d.status === 'pending');

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
    } catch {
      setCopied('');
    }
  };

  return (
    <div className="space-y-4">
      {pending.length ? (
        <Alert variant="info" title={`${pending.length} domain${pending.length > 1 ? 's' : ''} waiting for DNS`}>
          Ask the merchant to publish the TXT record below at their DNS provider. A domain routes to this store only after it is verified.
        </Alert>
      ) : null}
      <SectionCard title="Domains">
        <DataTable
          data={m.domains || []}
          columns={[
            { key: 'hostname', label: 'Hostname', render: (d) => <span className="font-medium break-all">{d.hostname}</span> },
            { key: 'status', label: 'Status', render: (d) => <StatusBadge status={d.status} /> },
            { key: 'verified_at', label: 'Verified', hideOnMobile: true, render: (d) => formatDate(d.verified_at) },
            { key: 'verification_record', label: 'DNS TXT record', wrap: true, render: (d) => (d.verification_record ? (
              <div className="space-y-1">
                <code className="block text-xs break-all">{d.verification_record}</code>
                <Button size="xs" variant="ghost" onClick={() => copy(d.verification_record)}>{copied === d.verification_record ? 'Copied' : 'Copy'}</Button>
              </div>
            ) : '—') },
            { key: 'actions', label: 'Action', render: (d) => (d.status === 'pending' && canManage ? (
              <Button size="sm" variant="outline" loading={busy === d.id} onClick={() => setConfirm(d)}>Mark verified</Button>
            ) : null) },
          ]}
        />
      </SectionCard>
      {confirm ? (
        <ConfirmDialog
          isOpen
          title={`Mark ${confirm.hostname} verified?`}
          description="Only do this after confirming the TXT record resolves. The domain starts routing to this merchant immediately."
          confirmLabel="Mark verified"
          loading={busy === confirm.id}
          onCancel={() => setConfirm(null)}
          onConfirm={async () => {
            await act(confirm.id, 'platformVerifyDomain', { domainId: confirm.id });
            setConfirm(null);
          }}
        />
      ) : null}
    </div>
  );
}
